import React, { useEffect, useState } from 'react';
import ModalOverlay from '../../shared/ModalOverlay';
import { Report, DistroList } from '../../types';

interface SendReportModalProps {
  active: boolean;
  onClose: () => void;
  report: Report | null;
  distro: DistroList | null;
  onSend: (to: string[], subject: string) => Promise<void> | void;
}

/**
 * Display-only sender. The actual From is enforced server-side (REPORTS_SENDER
 * at an SES-verified domain); this is purely informational. When configured, the
 * exact address is shown; otherwise a neutral label is displayed.
 */
const APP_SENDER_DISPLAY = process.env.REACT_APP_REPORTS_SENDER || 'LOFT Reports mailbox';

/** Parse a comma/newline/semicolon-separated recipient string into a clean list. */
function parseRecipients(value: string): string[] {
  return Array.from(new Set(value.split(/[\s,;]+/).map((v) => v.trim()).filter(Boolean)));
}

const SendReportModal: React.FC<SendReportModalProps> = ({ active, onClose, report, distro, onSend }) => {
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (active && report) {
      setTo((distro?.emails || []).map((e) => e.email).filter(Boolean).join(', '));
      setSubject(report.title);
    }
  }, [active, report, distro]);

  if (!report) return null;

  const recipients = parseRecipients(to);
  const canSend = recipients.length > 0 && !sending;

  const handleSend = async (): Promise<void> => {
    setSending(true);
    try {
      await onSend(recipients, subject.trim() || report.title);
    } finally {
      setSending(false);
    }
  };

  return (
    <ModalOverlay active={active} onClose={onClose}>
      <div className="modal modal-narrow">
        <div className="modal-header">
          <h2>Send Report</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="sched-fields">
          <div className="form-group">
            <label>Report</label>
            <input type="text" value={`${report.entity_name} — ${report.period_label}`} readOnly tabIndex={-1} />
          </div>
          <div className="form-group">
            <label>From (sender)</label>
            <input type="text" value={APP_SENDER_DISPLAY} readOnly tabIndex={-1} />
            <span style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>
              Sent from the LOFT reports mailbox. Replies go to you.
            </span>
          </div>
          <div className="form-group">
            <label>To — from the “{report.entity_name}” distribution list (editable)</label>
            <textarea rows={3} value={to} onChange={(e) => setTo(e.target.value)} placeholder="comma-separated email addresses" />
          </div>
          <div className="form-group">
            <label>Subject</label>
            <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
        </div>
        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={sending}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={handleSend} disabled={!canSend}>
            {sending ? 'Sending…' : `Send to ${recipients.length} recipient(s)`}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
};

export default SendReportModal;
